import { Link } from 'react-router-dom';
import { Footer } from '@/components/layout';

/**
 * About Page
 *
 * Public marketing page describing FlowIO features and use cases.
 * This page is accessible without authentication.
 */
export function AboutPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-primary-600">
              FlowIO
            </Link>
            <nav className="flex gap-4">
              <Link
                to="/login"
                className="text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Get Started
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <article className="bg-white rounded-lg shadow-sm p-8 sm:p-12">
          {/* Title */}
          <header className="mb-10 pb-8 border-b border-gray-200">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              About FlowIO
            </h1>
            <p className="text-xl text-gray-600">
              See where your money flows. Take control of your finances.
            </p>
          </header>

          {/* Introduction */}
          <section className="mb-10">
            <p className="text-gray-700 leading-relaxed mb-4">
              FlowIO is a personal finance application that helps you understand and
              manage your money. Connect your accounts, import your data, and visualize
              your finances with intuitive Sankey flow diagrams that show exactly where
              your money comes from and where it goes.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Whether you are tracking daily expenses, managing a household budget, or
              monitoring your investment portfolio, FlowIO gives you the tools you need
              to make informed financial decisions.
            </p>
          </section>

          {/* Features Section */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Features
            </h2>
            <div className="grid gap-6 sm:grid-cols-2">
              {/* Automated Bank Connections */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Automated Bank Connections</h3>
                  <p className="text-sm text-gray-600">
                    Connect securely via Plaid and Akoya. Your transactions sync automatically.
                  </p>
                </div>
              </div>

              {/* Manual Transaction Entry */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Manual Transaction Entry</h3>
                  <p className="text-sm text-gray-600">
                    Add transactions manually for accounts not supported by aggregators.
                  </p>
                </div>
              </div>

              {/* Document Import */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Document Import</h3>
                  <p className="text-sm text-gray-600">
                    Upload bank statements and CSV files to import historical data.
                  </p>
                </div>
              </div>

              {/* Sankey Flow Visualization */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Sankey Flow Visualization</h3>
                  <p className="text-sm text-gray-600">
                    See your money flow through interactive diagrams. Drill down into categories.
                  </p>
                </div>
              </div>

              {/* Multi-Account Support */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Multi-Account Support</h3>
                  <p className="text-sm text-gray-600">
                    Track checking, savings, credit cards, loans, and investments in one place.
                  </p>
                </div>
              </div>

              {/* Household Sharing */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Household Sharing</h3>
                  <p className="text-sm text-gray-600">
                    Share finances with family members. Each person gets their own login.
                  </p>
                </div>
              </div>

              {/* Transaction Categorization */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Transaction Categorization</h3>
                  <p className="text-sm text-gray-600">
                    Automatic categorization with AI. Override and customize as needed.
                  </p>
                </div>
              </div>

              {/* Allocation Tracking */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Allocation Tracking</h3>
                  <p className="text-sm text-gray-600">
                    Set targets and track actual allocation across asset classes.
                  </p>
                </div>
              </div>

              {/* Holdings Tracking */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Holdings Tracking</h3>
                  <p className="text-sm text-gray-600">
                    View individual holdings across all your investment accounts.
                  </p>
                </div>
              </div>

              {/* Portfolio Targets */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Portfolio Targets</h3>
                  <p className="text-sm text-gray-600">
                    Define your ideal allocation and see how your portfolio compares.
                  </p>
                </div>
              </div>

              {/* Mobile Apps */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Mobile Apps</h3>
                  <p className="text-sm text-gray-600">
                    Native iOS and Android apps with biometric authentication.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Use Cases Section */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Use Cases
            </h2>
            <div className="space-y-6">
              {/* Expense Tracking */}
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Expense Tracking</h3>
                <p className="text-gray-700">
                  See where your money goes with flow visualizations. FlowIO automatically
                  categorizes your transactions and shows you spending patterns across
                  categories, merchants, and time periods. Identify areas where you can
                  cut back and track progress toward your goals.
                </p>
              </div>

              {/* Portfolio Tracking */}
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Portfolio Tracking</h3>
                <p className="text-gray-700">
                  Monitor holdings and allocation across all your investment accounts.
                  FlowIO aggregates data from brokerage accounts, retirement accounts,
                  and other investment vehicles to give you a complete picture of your
                  portfolio allocation.
                </p>
              </div>

              {/* Retirement Coverage */}
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Retirement Coverage</h3>
                <p className="text-gray-700">
                  Set portfolio targets and track progress toward your retirement goals.
                  Define your ideal asset allocation and see how your current holdings
                  compare. FlowIO helps you stay on track with your long-term financial
                  plan.
                </p>
              </div>

              {/* Any Combination */}
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Any Combination</h3>
                <p className="text-gray-700">
                  Mix automated and manual entry to fit your workflow. Connect the accounts
                  you want to sync automatically, manually enter cash transactions, and
                  import historical data from statements. FlowIO adapts to how you manage
                  your finances.
                </p>
              </div>
            </div>
          </section>

          {/* Call to Action */}
          <div className="mt-12 pt-8 border-t border-gray-200 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Ready to get started?
            </h2>
            <p className="text-gray-700 mb-6">
              Create a free account and start tracking your finances today.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-sm transition-colors"
            >
              Get Started Free
            </Link>
          </div>
        </article>

        {/* Related Links */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <Link to="/security" className="hover:text-gray-700 hover:underline">
            Security
          </Link>
          <span className="mx-3">|</span>
          <Link to="/privacy" className="hover:text-gray-700 hover:underline">
            Privacy Policy
          </Link>
          <span className="mx-3">|</span>
          <Link to="/terms" className="hover:text-gray-700 hover:underline">
            Terms of Service
          </Link>
        </div>
      </main>

      {/* Standard Footer (WP-LANDING-4) */}
      <Footer />
    </div>
  );
}

export default AboutPage;
